import type { AxiosInstance } from "axios";
import type { ScopeCustomer, User } from "../types";

/**
 * Pluggable users data source for the Tags Manager "Users" step (wizard §3).
 *
 * Uses the same platform APIs as Account → Users and Notifications V2:
 * - `GET /tenant/customers?tenantId=` — customers under a partner tenant
 * - `GET /auth0/users/list/?tenantId=` — Auth0-backed users for that tenant
 *
 * Tag ↔ user persistence remains out of scope per `TAGS-API-CONTRACT.md` §8;
 * this module only powers the picker with real directory data.
 */
export interface UsersDataSource {
  /** Partners visible to a Global Admin (for the wizard's Partner selector). */
  listPartnersInScope: (api: AxiosInstance) => Promise<ScopeCustomer[]>;
  /** Customers visible to the current viewer for the picker dropdown. */
  listCustomersInScope: (
    api: AxiosInstance,
    partnerTenantId: number | null
  ) => Promise<ScopeCustomer[]>;
  /** Users belonging to a specific customer (chopper tenant id). */
  listUsersForCustomer: (
    api: AxiosInstance,
    customerTenantId: number,
    opts?: { search?: string }
  ) => Promise<User[]>;
}

interface CustomerRow {
  id: number;
  nameDisplay: string;
}

/** Row shape from `GET /auth0/users/list/` (Auth0 user + DB-enriched fields). */
interface RawAuth0UserRow {
  user_id?: string;
  email?: string;
  name?: string;
}

function extractUsersFromResponse(data: unknown): RawAuth0UserRow[] {
  if (!data || typeof data !== "object") return [];
  const o = data as Record<string, unknown>;
  if (Array.isArray(o.users)) return o.users as RawAuth0UserRow[];
  if (
    o.data &&
    typeof o.data === "object" &&
    Array.isArray((o.data as Record<string, unknown>).users)
  ) {
    return (o.data as { users: RawAuth0UserRow[] }).users;
  }
  return [];
}

export const usersDataSource: UsersDataSource = {
  listPartnersInScope: async (api) => {
    try {
      const resp = await api.get<{ data?: CustomerRow[] }>("/tenant/partners");
      const raw = resp.data?.data;
      if (!Array.isArray(raw)) return [];
      return raw.map((p) => ({ id: p.id, name: p.nameDisplay }));
    } catch {
      return [];
    }
  },

  listCustomersInScope: async (api, partnerTenantId) => {
    if (!partnerTenantId) return [];
    try {
      const resp = await api.get<{ data?: CustomerRow[] }>(
        "/tenant/customers",
        { params: { tenantId: partnerTenantId } }
      );
      const raw = resp.data?.data;
      if (!Array.isArray(raw)) return [];
      return raw.map((c) => ({ id: c.id, name: c.nameDisplay }));
    } catch {
      return [];
    }
  },

  listUsersForCustomer: async (api, customerTenantId, opts) => {
    if (!customerTenantId) return [];
    const resp = await api.get<unknown>("/auth0/users/list/", {
      params: { tenantId: customerTenantId },
    });
    const userList = extractUsersFromResponse(resp.data);
    let users: User[] = userList
      .filter((u) => Boolean(u.email && u.user_id))
      .map((u) => ({
        auth0Id: u.user_id as string,
        email: u.email as string,
        displayName: u.name?.trim() ? u.name.trim() : null,
        customerId: customerTenantId,
      }));
    const q = opts?.search?.trim().toLowerCase();
    if (q) {
      users = users.filter((u) =>
        [u.email, u.displayName ?? ""].join(" ").toLowerCase().includes(q)
      );
    }
    return users;
  },
};

/**
 * No local customer directory cache — names come from `listCustomersInScope`
 * or SideNav `tenantName`. Returns null so callers fall back to `Customer {id}`.
 */
export function lookupCustomerName(customerId: number): string | null {
  void customerId;
  return null;
}
